import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react'
import { ExtractedResponse } from '../../externals/api/applyRules'
import { Pagination } from '../../externals/api/config'
import {
  create as createApi,
  get as getApi, getExam as getExamApi, getLastExtraction as getLastExtractionApi, getOne as getOneApi,
  GraphExam,
  Patient, PatientCreate, PatientEdit, PatientFilter, saveExam as saveExamApi,
  SaveIntoPatient,
  SaveIntoPatientResponse,
  update
} from '../../externals/api/patients'
import { useAlertHandler } from '../alertHandler'

interface IPatients {
  patientsList: Patient[]
  patients: Pagination<Patient>
  patient: Patient
  processedExam: ExtractedResponse
  exams: GraphExam
  get: (filter?: PatientFilter) => Promise<Pagination<Patient>>
  getOne: (patientId: string) => Promise<Patient>
  getList: () => Promise<Patient[]>
  saveExam: (params: SaveIntoPatient) => Promise<SaveIntoPatientResponse>
  edit: (params: PatientEdit) => Promise<Patient>
  create: (patient: PatientCreate) => Promise<Patient>
  getLastProcessedExam: (patientId: string) => Promise<ExtractedResponse>
  getExam: (patientId: string, examId: string) => Promise<GraphExam>
  clearLastProcessedExam: () => void
}

const PatientsContext = createContext<IPatients>({} as IPatients)

export const usePatients = (): IPatients => {
  const context = useContext(PatientsContext)
  if (!context) {
    throw new Error('usePatients must be used within an PatientsContext')
  }

  return context
}

export const PatientsProvider: FC = ({ children }): ReactElement => {
  const [patientsList, setPatientList] = useState<Patient[]>([])
  const [exams, setExams] = useState<GraphExam>({} as GraphExam)
  const [patient, setPatient] = useState<Patient>({} as Patient)
  const [processedExam, setProcessedExam] = useState<ExtractedResponse>({} as ExtractedResponse)
  const [patients, setPatients] = useState<Pagination<Patient>>({
    page: 1,
    docs: []
  } as Pagination<Patient>)
  const { showError } = useAlertHandler()

  const get = useCallback(async (filter?: PatientFilter): Promise<Pagination<Patient>> => {
    try {
      const response = await getApi({ ...filter, page: filter?.page || 1 })

      setPatients(response)
      // setPatients(old => ({
      //   ...response,
      //   docs: response.page === 1 ? response.docs : [...old.docs, ...response.docs]
      // }))

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPatients, showError])

  const getLastProcessedExam = useCallback(async (patientId: string): Promise<ExtractedResponse> => {
    try {
      const response = await getLastExtractionApi(patientId)

      setProcessedExam(response)

      return response
    } catch (err) {
      setProcessedExam({} as ExtractedResponse)
      showError(err)

      throw err
    }
  }, [setPatients, showError])

  const clearLastProcessedExam = useCallback((): void => {
    setProcessedExam({} as ExtractedResponse)
  }, [showError])

  const getOne = useCallback(async (patientId: string): Promise<Patient> => {
    try {
      const response = await getOneApi(patientId)

      setPatient(response)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPatient, showError])

  const getList = useCallback(async (): Promise<Patient[]> => {
    try {
      const response = await getApi({ limit: 100000000 })
      setPatientList(response.docs)

      return response.docs
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPatients, showError])

  const getExam = useCallback(async (patientId: string, examId: string): Promise<GraphExam> => {
    try {
      const response = await getExamApi({ patientId, examId })
      setExams(response)

      return response
    } catch (err) {
      showError(err)
      setExams({} as GraphExam)

      throw err
    }
  }, [setExams, showError])

  const create = useCallback(async (params: PatientCreate): Promise<Patient> => {
    try {
      const response = await createApi(params)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [showError])

  const edit = useCallback(async (params: PatientEdit): Promise<Patient> => {
    try {
      const response = await update(params)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [showError])

  const saveExam = useCallback(async (params: SaveIntoPatient): Promise<SaveIntoPatientResponse> => {
    try {
      const response = await saveExamApi(params)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [showError])

  return (
    <PatientsContext.Provider
      value={{
        patient,
        patients,
        patientsList,
        processedExam,
        exams,
        get,
        create,
        getList,
        saveExam,
        getOne,
        getLastProcessedExam,
        getExam,
        clearLastProcessedExam,
        edit
      }}
    >
      {children}
    </PatientsContext.Provider>
  )
}
